import { Classes } from "@blueprintjs/core";
import { DATE_FORMAT_WITH_TIME, OrgRole, UserId } from "@hex/common";
import moment from "moment";
import React from "react";
import styled from "styled-components";

import { HexTooltip } from "../../hex-components";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { HumanDate } from "../common/HumanDate";
import { UserAvatar } from "../user/UserAvatar";

import { useCommentUserInfoQuery } from "./Comment.generated";

const CommentAuthorWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  margin-right: auto;
`;

const AuthorName = styled.div`
  margin-right: 6px;

  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
  line-height: 18px;
`;

const CommentDate = styled.div`
  flex: none;

  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: 17px;

  cursor: default;
`;

const DateTooltip = styled(HexTooltip)`
  && {
    display: flex;
    flex: none;
  }
`;

export interface CommentAuthorProps {
  readonly creatorId: UserId;
  readonly createdDate: Date;
  readonly editedTextDate: Date | null;
}

export const CommentAuthor: React.ComponentType<CommentAuthorProps> =
  React.memo(function CommentAuthor({
    createdDate,
    creatorId,
    editedTextDate,
  }) {
    const { data: userData } = useCommentUserInfoQuery({
      variables: { userId: creatorId },
      fetchPolicy: "cache-first",
    });

    const currentUser = useCurrentUser();
    const isAnonymous = currentUser?.orgRole === OrgRole.ANONYMOUS;
    const isCommentEdited = editedTextDate != null;
    return (
      <CommentAuthorWrapper>
        <UserAvatar
          css={`
            margin-right: 5px;
          `}
          email={isAnonymous ? undefined : userData?.userById.email}
          imageUrl={userData?.userById.imageUrl ?? undefined}
          name={userData?.userById.name ?? undefined}
          size={18}
        />
        <AuthorName
          className={Classes.TEXT_OVERFLOW_ELLIPSIS}
          title={userData?.userById.name || ""}
        >
          {userData?.userById.name}
        </AuthorName>
        <DateTooltip
          content={
            !isCommentEdited ? (
              <div>{moment(createdDate).format(DATE_FORMAT_WITH_TIME)}</div>
            ) : (
              <div>
                <div>
                  Created: {moment(createdDate).format(DATE_FORMAT_WITH_TIME)}
                </div>
                <div>
                  Edited: {moment(editedTextDate).format(DATE_FORMAT_WITH_TIME)}
                </div>
              </div>
            )
          }
        >
          <CommentDate>
            <HumanDate
              date={isCommentEdited ? editedTextDate : createdDate}
              title={false}
            />
            {isCommentEdited && <span> (edited)</span>}
          </CommentDate>
        </DateTooltip>
      </CommentAuthorWrapper>
    );
  });
